import { useCallback, useContext, useEffect, useState } from "react"
import { DateTime, Settings } from "luxon"

// Context
import { UserContext } from "../../../context/userContext"

//Helpers
import { LogError } from "../../../helpers/logger"
import { LoaderMedium } from "../../../components/Loaders"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { SelectButton } from "primereact/selectbutton"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"
import FormikErrorNotification from "../../../helpers/formikInputs/FormikErrorNotification"
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"
import FormikCheckBox from "../../../helpers/formikInputs/FormikCheckboxInput"

//styling
import myStyles from "../../../styles/assessments/AssessmentScheduleForm.module.css"
import assessStyles from "../../../styles/assessments/Assessment.module.css"

//Assessment components
import {
  assessmentsApiScheduleGet,
  assessmentsApiSchedulePost,
  assessmentsApiScheduleUpdatePut,
} from "../assessmentsApi"
import AssessmentSelector from "./AssessmentSelector"
import { getLocalTimeValue } from "./scheduleHelpers"
import AssessmentScheduleFormTypeSelector from "./AssessmentScheduleFormTypeSelector"

export default function AssessmentScheduleForm({
  showForm,
  onCancel,
  onSaved,
  scheduleId,
  assessments,
  telerecoveryIsActive,
  staffList,
}) {
  const userCtx = useContext(UserContext)
  const singleScheduleType = "single"
  const dailyScheduleType = "daily"
  const defaultTargetType = "participant"

  const [timeZones, setTimeZones] = useState([
    { name: "Eastern Standard Time", code: "America/New_York" },
    { name: "Pacific Standard Time", code: "America/Los_Angeles" },
    { name: "Mountain Standard Time", code: "America/Phoenix" },
    { name: "Mountain Daylight Savings Time", code: "America/Denver" },
    { name: "Central Standard Time", code: "America/Chicago" },
    { name: "Hawaii/Aleutian Standard Time", code: "Pacific/Honolulu" },
    { name: "Alaskan Standard Time", code: "America/Anchorage" },
  ])

  const [isLoading, setIsLoading] = useState(false)
  const [isProcessingSubmit, setIsProcessingSubmit] = useState(false)
  const [toggleRefreshCron, setToggleRefreshCron] = useState(false)
  const [scheduleProfile, setScheduleProfile] = useState({
    name: "",
    organizationID: userCtx.session.user.organization_id,
    assignedStaffId: userCtx.session.user.staff_id,
    assignedParticipantId: userCtx.participant_id,
    assignedTarget: defaultTargetType,
    startDate: new Date(),
    endDate: new Date(),
    timezone: Settings.defaultZone.name,
    time: new Date(),
    scheduleType: singleScheduleType,
    cronSchedule: "",
    assessments: [],
    creatorUserId: userCtx.session.user.staff_id,
    CreatorUserName: `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
  })

  const loadProfile = useCallback(async () => {
    try {
      if (scheduleId) {
        setIsLoading(true)

        const result = await assessmentsApiScheduleGet(
          userCtx.tokens.getAssessmentsToken(),
          scheduleId,
        )

        result.startDate = DateTime.fromISO(result.startDate, {
          zone: "UTC",
        }).toJSDate()
        result.endDate = DateTime.fromISO(result.endDate, {
          zone: "UTC",
        }).toJSDate()

        const cronBits = result.cronSchedule.split(" ")
        let scheduledTime = DateTime.fromJSDate(result.startDate)
          .set({
            hour: cronBits[1],
            minute: cronBits[0],
            second: 0,
          })
          .setZone("UTC", { keepLocalTime: true })

        scheduledTime = scheduledTime.setZone(result.timezone || "UTC")

        const displayTime = DateTime.fromJSDate(result.startDate).set({
          hour: scheduledTime.hour,
          minute: scheduledTime.minute,
          second: 0,
        })

        result.time = displayTime.toJSDate()

        setScheduleProfile(result)
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to retrieve schedule profile".error)
      userCtx.setNotificationError("Unable to retrieve schedule profile")
      onCancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId])

  useEffect(() => {
    if (
      !timeZones.some((timezone) => timezone.code === Settings.defaultZone.name)
    ) {
      setTimeZones([
        ...timeZones,
        {
          code: Settings.defaultZone.name,
          name: Settings.defaultZone.name,
        },
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Please specify a name"),
    scheduleType: Yup.string().required("Please select a schedule type"),
    startDate: Yup.date().required("Please specify a start date"),
    endDate: Yup.date()
      .required("Please specify an end date")
      .min(
        Yup.ref("startDate"),
        "Please specify an end date that is after the start date",
      ),
    cronSchedule: Yup.string().required("Please specify a schedule"),
    assessments: Yup.array().min(1, "Please select an assessment"),
  })

  const handleFormSubmit = async (values) => {
    try {
      setIsProcessingSubmit(true)
      let payload = {
        ...values,
        assignedParticipantName: `${userCtx.participant_name_first} ${userCtx.participant_name_last}`,
      }

      let newEndDate = DateTime.fromJSDate(payload.endDate)
      newEndDate = newEndDate.set({ hour: 23, minute: 59, second: 59 })

      const timeValue = getLocalTimeValue(values.time, values.timezone)
      let startDate = DateTime.fromJSDate(payload.startDate)
      startDate = startDate.set({
        hour: timeValue.hour,
        minute: timeValue.minute,
        second: 0,
        millisecond: 0,
      })

      //If this is an edit and the start date/time has been edited. Check that it is not backdated
      if (
        (values.startDate !== scheduleProfile.startDate ||
          values.time !== scheduleProfile.time) &&
        startDate < DateTime.now()
      ) {
        userCtx.setNotificationError(
          "Unable to schedule an appointment in the past, please update the time or start date",
        )
        setIsProcessingSubmit(false)
        return false
      }

      payload.startDate = startDate.toUTC().toISO()
      payload.endDate = newEndDate.toUTC().toISO()
      payload.userId = userCtx.session.user.staff_id
      payload.userName = `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`

      if (payload.emailNotifications) {
        payload.assignedParticipantEmail = userCtx.participant_email_account
      }

      if (payload.smsNotifications) {
        payload.assignedParticipantSMSNumber = userCtx.participant_phone_primary
      }

      if (!payload.id) {
        const result = await assessmentsApiSchedulePost(
          userCtx.tokens.getAssessmentsToken(),
          payload,
        )
        if (result) {
          onSaved()
        } else {
          userCtx.setNotificationError(
            "Something went wrong while creating the schedule",
          )
        }
      } else {
        const result = await assessmentsApiScheduleUpdatePut(
          userCtx.tokens.getAssessmentsToken(),
          payload,
        )
        if (result) {
          onSaved()
        } else {
          userCtx.setNotificationError(
            "Something went wrong while updating the schedule",
          )
        }
      }

      setIsProcessingSubmit(false)
    } catch (error) {
      LogError("Unable to save schedule", error)
      userCtx.setNotificationError("Unable to save schedule")
      setIsProcessingSubmit(false)
    }
  }

  const getLocalTime = (time, timezone) => {
    if (timezone) {
      let newTime = getLocalTimeValue(time, timezone)
      return `Local time: ${newTime.toLocal().toFormat("t")}`
    } else {
      return undefined
    }
  }

  return (
    <Dialog
      header={`Scheduling new assessment`}
      visible={showForm}
      onHide={onCancel}
      className={myStyles.dialog}
    >
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <Formik
          initialValues={scheduleProfile}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            //If this is an edit session and the schedule values have changed, warn users
            if (
              values.id &&
              (values.startDate !== scheduleProfile.startDate ||
                values.endDate !== scheduleProfile.endDate ||
                values.time !== scheduleProfile.time ||
                values.timezone !== scheduleProfile.timezone ||
                values.scheduleType !== scheduleProfile.scheduleType ||
                values.cronSchedule !== scheduleProfile.cronSchedule)
            ) {
              confirmDialog({
                message: (
                  <>
                    <p>
                      The scheduling parameters have been updated and any
                      <b> incomplete</b> historic scheduled items will be
                      removed during save.
                    </p>
                    <br />
                    <p>Do you want to continue?</p>
                  </>
                ),
                header: "Update confirmation",
                icon: "pi pi-info-circle",
                defaultFocus: "reject",
                acceptClassName: "p-button-danger",
                accept: async () => {
                  await handleFormSubmit(values)
                },
              })
            } else {
              await handleFormSubmit(values)
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form autoComplete="off" className={myStyles.form}>
              <FormikErrorNotification />
              <ConfirmDialog />
              <div
                className={`${myStyles.layout} ${
                  values.scheduleType === singleScheduleType
                    ? myStyles.layoutSingle
                    : ""
                }`}
              >
                <div className={myStyles.layoutDetail}>
                  <div className={myStyles.participantDetailContainer}>
                    <span className={myStyles.participantDetailHeader}>
                      Participant name:
                    </span>
                    {` ${userCtx.participant_name_first} ${userCtx.participant_name_last}`}
                  </div>
                  <div className={myStyles.participantDetailContainer}>
                    <span className={myStyles.participantDetailHeader}>
                      Participant Id:
                    </span>{" "}
                    {` ${userCtx.participant_alias}`}
                  </div>
                </div>
                <div className={myStyles.layoutName}>
                  <FormikTextInput
                    name={"name"}
                    label={"Name"}
                    helpText={"The name of the schedule"}
                    placeholderText={"Enter name"}
                    disabled={isSubmitting || isProcessingSubmit}
                  />
                </div>
                <div className={myStyles.layoutAssessment}>
                  <AssessmentSelector
                    fieldName={"assessments"}
                    assessments={
                      assessments?.filter(
                        (assessment) => !assessment.isDisabled,
                      ) || []
                    }
                  />
                </div>
                <div className={myStyles.layoutAssign}>
                  <FormikDropDown
                    name={"assignedTarget"}
                    label={"Assign to"}
                    helpText={
                      "Select whether the scheduled task will be assigned to the participant or a staff user. If assigned to a participant, they will get a notification and task to complete this as an assessment on the due date. If assigned to a staff, they will get a notification and task to complete this as an ad hoc or session assessment on the due date."
                    }
                    options={[
                      { label: "Participant", value: defaultTargetType },
                      { label: "Staff", value: "staff" },
                    ]}
                    onChangeCallback={(value) => {
                      if (value === defaultTargetType) {
                        setFieldValue(
                          "assignedStaffId",
                          userCtx.session.user.staff_id,
                        )
                        setFieldValue("assignedStaffSMSNumber", undefined)
                        setFieldValue("assignedStaffEmail", undefined)
                      }
                    }}
                    disabled={isSubmitting || isProcessingSubmit}
                  />
                  {values.assignedTarget !== defaultTargetType ? (
                    <FormikDropDown
                      name={"assignedStaffId"}
                      label={"Select assignee"}
                      helpText={
                        "Select which staff user you want to assign this to, you can assign this to yourself or another staff user."
                      }
                      options={
                        staffList || [
                          {
                            staff_id: userCtx.session.user.staff_id,
                            staff_name: `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
                            staff_email_address:
                              userCtx.session.user.staff_email_address,
                            staff_phone_number:
                              userCtx.session.user.staff_phone_number,
                          },
                        ]
                      }
                      onChangeCallback={(value) => {
                        let assignedStaffSMSNumber =
                          userCtx.session.user.staff_phone_number
                        let assignedStaffEmail =
                          userCtx.session.user.staff_email_address

                        if (value !== userCtx.session.user.staff_id) {
                          const staff = staffList.find(
                            (staff) => staff.staff_id === value,
                          )

                          if (staff) {
                            assignedStaffSMSNumber = staff.staff_phone_number
                            assignedStaffEmail = staff.staff_email_address
                          } else {
                            userCtx.setNotificationError(
                              "Unable to find details for the selected staff, resetting to current staff account",
                            )
                            setFieldValue(
                              "assignedStaffId",
                              userCtx.session.user.staff_id,
                            )
                          }
                        }

                        setFieldValue(
                          "assignedStaffSMSNumber",
                          assignedStaffSMSNumber,
                        )
                        setFieldValue("assignedStaffEmail", assignedStaffEmail)
                      }}
                      optionLabel={"staff_name"}
                      optionValue={"staff_id"}
                      disabled={isSubmitting || isProcessingSubmit}
                    />
                  ) : null}
                </div>
                <div
                  className={`${myStyles.layoutSingle} ${assessStyles.selectButton}`}
                >
                  <label>Schedule type</label>
                  <div style={{ marginBottom: "10px" }}>
                    <small style={{ color: "gray" }}>
                      Specify if this will be a single scheduled assessment, or
                      a recurring series of assessments
                    </small>
                  </div>
                  <SelectButton
                    value={values.scheduleType === singleScheduleType}
                    onChange={(e) => {
                      if (e.value !== null) {
                        if (e.value) {
                          setFieldValue("scheduleType", singleScheduleType)

                          //If this is now a single schedule type, default the endDate to the startDate
                          if (
                            values.startDate?.getTime() !==
                            values.endDate?.getTime()
                          ) {
                            setFieldValue("endDate", values.startDate)
                          }
                        } else {
                          setFieldValue("scheduleType", dailyScheduleType)
                        }
                        setToggleRefreshCron(!toggleRefreshCron)
                      }
                    }}
                    options={[
                      { label: "Single scheduled assessment", value: true },
                      {
                        label: "Series of scheduled assessments",
                        value: false,
                      },
                    ]}
                    style={{ marginBottom: "10px" }}
                    tooltip={`Selected: ${
                      values.scheduleType === singleScheduleType
                        ? "Single scheduled assessment"
                        : "Series of scheduled assessments"
                    }`}
                    tooltipOptions={{ position: "bottom" }}
                  />
                </div>
                <div className={myStyles.layoutStart}>
                  <FormikCalendar
                    name={"startDate"}
                    label={
                      values.scheduleType === singleScheduleType
                        ? "Date"
                        : "Start date"
                    }
                    placeholderText={"Enter start date"}
                    onChangeCallBack={(date) => {
                      if (
                        values.scheduleType === singleScheduleType ||
                        values.endDate < date
                      ) {
                        setFieldValue("endDate", date)
                      }
                    }}
                    disabled={isSubmitting || isProcessingSubmit}
                    minDate={DateTime.now().toJSDate()}
                  />
                </div>
                <div className={myStyles.layoutEnd}>
                  <FormikCalendar
                    name={"endDate"}
                    label={"End date"}
                    placeholderText={"Enter end date"}
                    minDate={values.startDate}
                    disabled={
                      isSubmitting ||
                      isProcessingSubmit ||
                      values.scheduleType === singleScheduleType
                    }
                  />
                </div>
                <div className={myStyles.layoutTimeZone}>
                  <FormikDropDown
                    name={"timezone"}
                    label={"Timezone"}
                    options={timeZones}
                    optionLabel={"name"}
                    optionValue={"code"}
                    onChangeCallback={() => {
                      setToggleRefreshCron(!toggleRefreshCron)
                    }}
                    disabled={isSubmitting || isProcessingSubmit}
                  />
                </div>
                <div className={myStyles.layoutTime}>
                  <FormikCalendar
                    name={"time"}
                    label={"Time"}
                    placeholderText={"Enter time"}
                    onChangeCallBack={(timeValue) => {
                      setToggleRefreshCron(!toggleRefreshCron)
                    }}
                    showTime={true}
                    timeOnly={true}
                    helpText={getLocalTime(values.time, values.timezone)}
                    disabled={isSubmitting || isProcessingSubmit}
                  />
                </div>
                {values.scheduleType !== singleScheduleType ? (
                  <div className={myStyles.layoutPeriod}>
                    <FormikDropDown
                      name={"scheduleType"}
                      label={"Schedule interval"}
                      helpText={"Select the interval for this schedule"}
                      options={[
                        { label: "Daily", value: "daily" },
                        { label: "Weekly", value: "weekly" },
                        { label: "Biweekly", value: "biweekly" },
                        { label: "Monthly", value: "monthly" },
                        { label: "Yearly", value: "yearly" },
                      ]}
                      disabled={isSubmitting || isProcessingSubmit}
                    />
                  </div>
                ) : null}
                <div className={myStyles.layoutOptions}>
                  <AssessmentScheduleFormTypeSelector
                    type={values.scheduleType}
                    time={values.time}
                    timezone={values.timezone}
                    disable={isSubmitting || isProcessingSubmit}
                    toggleRefreshCron={toggleRefreshCron}
                  />
                </div>
                <div className={myStyles.layoutEmail}>
                  <div className={myStyles.notificationContainer}>
                    <FormikCheckBox
                      name={"emailNotifications"}
                      className={myStyles.notificationCheckbox}
                      disabled={
                        isSubmitting ||
                        isProcessingSubmit ||
                        !userCtx?.participant_email_account
                      }
                      tooltip={
                        !userCtx?.participant_email_account
                          ? "No participant e-mail available"
                          : ""
                      }
                    />
                    <label className={myStyles.notificationLabel}>
                      Send E-mail Notifications
                    </label>
                  </div>
                </div>
                {telerecoveryIsActive ? (
                  <div className={myStyles.layoutSms}>
                    <div className={myStyles.notificationContainer}>
                      <FormikCheckBox
                        name={"smsNotifications"}
                        className={myStyles.notificationCheckbox}
                        disabled={
                          isSubmitting ||
                          isProcessingSubmit ||
                          !userCtx?.participant_phone_primary
                        }
                        tooltip={
                          !userCtx?.participant_phone_primary
                            ? "No participant phone available"
                            : ""
                        }
                      />
                      <label className={myStyles.notificationLabel}>
                        Send SMS Notifications
                      </label>
                    </div>
                  </div>
                ) : null}
                <div className={myStyles.layoutButtons}>
                  <Button
                    label="Submit"
                    type="submit"
                    disabled={isSubmitting || isProcessingSubmit}
                    loading={isSubmitting || isProcessingSubmit}
                  />
                  <Button
                    label="Cancel"
                    type="button"
                    onClick={() => {
                      onCancel()
                    }}
                    disabled={isSubmitting || isProcessingSubmit}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  )
}
